import { CORNER_ORDER, type Corner } from "./overlayConstants";
import type { WaveformJson } from "../api/runs";

export type OverlayData = {
  ok: true;
  data: (number[] | (number | null)[])[];
  yDomain: [number, number];
  hasRecorded: Record<Corner, boolean>;
};

export type OverlayDataEmpty = {
  ok: false;
  reason: "empty" | "length_mismatch" | "no_finite_y";
};

const M_TO_MM = 1000;
const toMm = (arr: number[]): number[] => arr.map((v) => v * M_TO_MM);

export function buildOverlayData(
  wf: WaveformJson,
): OverlayData | OverlayDataEmpty {
  const n = wf.time_s.length;
  if (n === 0) return { ok: false, reason: "empty" };

  for (const c of CORNER_ORDER) {
    if (wf.predicted[c].length !== n) {
      return { ok: false, reason: "length_mismatch" };
    }
  }

  // Length checks run on the RAW metre arrays; mm conversion happens after.
  const recPerCorner: Record<Corner, number[] | null> = {
    lf: null, rf: null, lr: null, rr: null,
  };
  if (wf.recorded !== null) {
    for (const c of CORNER_ORDER) {
      const arr = wf.recorded[c];
      if (arr === null) continue;
      if (arr.length !== n) {
        return { ok: false, reason: "length_mismatch" };
      }
      recPerCorner[c] = toMm(arr);
    }
  }

  const hasRecorded = Object.fromEntries(
    CORNER_ORDER.map((c) => [c, recPerCorner[c] !== null]),
  ) as Record<Corner, boolean>;

  const yDomain = computeYDomain([
    ...CORNER_ORDER.map((c) => toMm(wf.predicted[c])),
    ...CORNER_ORDER.map((c) => recPerCorner[c]),   // already mm
  ]);
  if (yDomain === null) return { ok: false, reason: "no_finite_y" };

  const nulls: (number | null)[] = new Array(n).fill(null);
  const data: (number[] | (number | null)[])[] = [
    wf.time_s,                                      // time stays seconds
    ...CORNER_ORDER.map((c) => recPerCorner[c] ?? nulls),
    ...CORNER_ORDER.map((c) => toMm(wf.predicted[c])),
  ];

  return { ok: true, data, yDomain, hasRecorded };
}

function computeYDomain(
  arrays: (number[] | null)[],
): [number, number] | null {
  let yMin = Infinity, yMax = -Infinity;
  for (const arr of arrays) {
    if (arr === null) continue;
    for (const v of arr) {
      if (Number.isFinite(v)) {
        if (v < yMin) yMin = v;
        if (v > yMax) yMax = v;
      }
    }
  }
  if (!Number.isFinite(yMin) || !Number.isFinite(yMax)) return null;
  const span = Math.max(1e-9, yMax - yMin);
  const pad = span * 0.05;
  return [yMin - pad, yMax + pad];
}
